// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import type { ColumnDef } from "@tanstack/react-table";
import { RotateCcw, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { CSVExportButton } from "@/components/csv-export-button";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { DrilldownChartControls } from "@/components/metrics/drilldown-chart-controls";
import { DrilldownChartErrorBoundary } from "@/components/metrics/drilldown-chart-error-boundary";
import { DrilldownLimitAlert } from "@/components/metrics/drilldown-limit-alert";
import { DrilldownTimeSeriesChart } from "@/components/metrics/drilldown-timeseries-chart";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { SortableTable } from "@/components/sortable-table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSalesperson } from "@/hooks/api/use-salespeople";
import { useSalespersonMetricDrilldown } from "@/hooks/api/use-salesperson-metrics";
import { useTableCSVExport } from "@/hooks/use-csv-export";
import { useDrilldownChartState } from "@/hooks/use-drilldown-chart-state";
import {
  type AccountData,
  formatAccountSourceRecordIdsForCSV,
  formatAccountsForCSV,
} from "@/lib/account-data";
import { formatDate, formatDateForFilename } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";
import { isNumber } from "@/lib/is-number";
import {
  formatOpportunitiesForCSV,
  getOpportunityData,
} from "@/lib/opportunity-display-utils";
import { cn } from "@/lib/utils";
import type {
  SalespersonDrilldownResult,
  SalespersonMetrics,
} from "@/types/salesperson-types";
import type { UnifiedTableColumn } from "@/types/table-column";
import { formatRelationSource, formatTouchpointType } from "@/utils/formatters";
import { CompactMetricCard } from "./components/compact-metric-card";
import { SalespersonEngagementChart } from "./salesperson-engagement-chart";
import {
  METRIC_CONFIG,
  type SalespersonMetric,
} from "./salesperson-metrics-config";

// Use predefined pill colors from CATEGORY_STYLES
const OPPORTUNITY_PILL_CLASSES = CATEGORY_STYLES.Opportunity.pillColor;
const ACCOUNT_PILL_CLASSES = CATEGORY_STYLES.Account.pillColor;

interface MetricDrilldownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string | null;
  metric: SalespersonMetric | null;
  touchpointTypes: string[];
  filters?: MetricsFilters;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
  includeBids?: boolean;
  metrics?: SalespersonMetrics;
  drillDownLimit?: number;
  loadingStates?: Record<
    SalespersonMetric,
    { isLoading: boolean; isError: boolean }
  >;
}

// Type for common properties across all drilldown item types
type DrilldownItem = SalespersonDrilldownResult;

// Helper function to create consistent account link rendering
const renderAccountLink = (
  item: SalespersonDrilldownResult,
): React.ReactNode => {
  if (!item) {
    return <span>-</span>;
  }

  // Ensure we have an account_id
  if (!item.account_id) {
    return <span>{item.name || item.account_name || "Unknown"}</span>;
  }

  const displayName = item.name || item.account_name || "Unknown";

  // Create link to account explorer
  const explorerUrl = `/accounts/${item.account_id}/explorer`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-pill
          className={cn(
            "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
            ACCOUNT_PILL_CLASSES,
          )}
        >
          {displayName}
        </a>
      </TooltipTrigger>
      <TooltipContent>
        <p>View this account in Account Explorer</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Helper function to create consistent opportunity link rendering
const renderOpportunityLink = (
  item: SalespersonDrilldownResult,
): React.ReactNode => {
  if (!item) {
    return <span>-</span>;
  }

  // Ensure we have required fields
  if (!item.opportunity_id || !item.account_id) {
    return <span>{item.name || "Unknown"}</span>;
  }

  const displayName = item.name || "Unknown";
  return (
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={`/accounts/${item.account_id}/explorer?opportunityId=${item.opportunity_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:text-muted-foreground hover:bg-muted rounded px-2 py-1 -mx-2 -my-1 transition-colors group/name"
          >
            <span>{displayName}</span>
            <Search className="ml-2 h-4 w-4 text-muted-foreground transition-colors opacity-0 group-hover/name:opacity-100" />
          </a>
        </TooltipTrigger>
        <TooltipContent>
          <p>View this opportunity in Account Explorer</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

// Define columns for each metric type
const METRIC_COLUMNS: Record<SalespersonMetric, ColumnDef<DrilldownItem>[]> = {
  accounts_engaged: [
    {
      accessorKey: "name",
      header: "Account Name",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "source_record_id",
      header: "Account Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "total_engagements",
      header: "Total Engagements",
    },
    {
      accessorKey: "first_engagement_date",
      meta: { dataType: "date" },
      header: "First engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "last_engagement_date",
      meta: { dataType: "date" },
      header: "Last engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
  ],
  total_engagements: [
    {
      accessorKey: "engagement_date",
      meta: { dataType: "date" },
      header: "Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "touchpoint_type",
      header: "Type",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatTouchpointType(value) : "-";
      },
    },
    {
      accessorKey: "person",
      header: "Person",
      cell: ({ row }) => {
        const firstName = row.original.first_name as string | null;
        const lastName = row.original.last_name as string | null;
        return (
          <span>
            {firstName && lastName ? `${firstName} ${lastName}` : "Unknown"}
          </span>
        );
      },
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "email_address",
      header: "Email",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value || "-";
      },
    },
    {
      accessorKey: "accounts",
      header: "Accounts",
      cell: ({ row }) => {
        const accounts = row.original.accounts as
          | Array<{ id: string; name: string; source_record_id?: string }>
          | undefined;
        if (!accounts || accounts.length === 0) {
          return "-";
        }
        return (
          <span>
            {accounts.map((account, idx) => (
              <React.Fragment key={account.id}>
                {idx > 0 && ", "}
                {renderAccountLink({
                  account_id: account.id,
                  name: account.name,
                })}
              </React.Fragment>
            ))}
          </span>
        );
      },
      meta: {
        exportValue: (item: DrilldownItem) => {
          const accounts = item.accounts as Array<{ name: string }> | undefined;
          if (!accounts || accounts.length === 0) return "";
          return accounts
            .map((a) => `"${a.name.replace(/"/g, '""')}"`)
            .join(", ");
        },
      },
    },
    {
      accessorKey: "account_source_record_ids",
      header: "Account Source IDs",
      meta: {
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const accounts = item.accounts as
            | Array<{ source_record_id?: string }>
            | undefined;
          if (!accounts || accounts.length === 0) return "";
          return accounts
            .map((a) => a.source_record_id || "")
            .filter((id) => id !== "")
            .join(", ");
        },
      },
    },
  ],
  people_engaged: [
    {
      accessorKey: "contact",
      header: "Contact",
      cell: ({ row }) => {
        const firstName = row.original.first_name as string | null;
        const lastName = row.original.last_name as string | null;
        const email = row.original.email_address as string | null;
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {firstName && lastName ? `${firstName} ${lastName}` : "Unknown"}
            </span>
            <span className="text-sm text-muted-foreground">
              {email || "No email"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "email_address",
      header: "Email Address",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "job_title",
      header: "Job Title",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value || "-";
      },
    },
    {
      accessorKey: "accounts",
      header: "Accounts",
      enableSorting: false,
      cell: ({ row }) => {
        const accounts = row.original.accounts as
          | Array<{ id: string; name: string; source_record_id?: string }>
          | undefined;
        if (!accounts || accounts.length === 0) {
          return "-";
        }
        // Render all accounts as comma-separated links
        return (
          <span>
            {accounts.map((account, idx) => (
              <React.Fragment key={account.id}>
                {idx > 0 && ", "}
                {renderAccountLink({
                  account_id: account.id,
                  name: account.name,
                })}
              </React.Fragment>
            ))}
          </span>
        );
      },
      meta: {
        exportValue: (item: DrilldownItem) => {
          const accounts = item.accounts as Array<{ name: string }> | undefined;
          if (!accounts || accounts.length === 0) return "";
          return accounts
            .map((a) => `"${a.name.replace(/"/g, '""')}"`)
            .join(", ");
        },
      },
    },
    {
      accessorKey: "account_source_record_ids",
      header: "Account Source IDs",
      meta: {
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const accounts = item.accounts as
            | Array<{ source_record_id?: string }>
            | undefined;
          if (!accounts || accounts.length === 0) return "";
          return accounts
            .map((a) => a.source_record_id || "")
            .filter((id) => id !== "")
            .join(", ");
        },
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
  ],
  opportunities_influenced: [
    {
      accessorKey: "name",
      header: "Opportunity",
      cell: ({ row }) => renderOpportunityLink(row.original),
    },
    {
      accessorKey: "opportunity_source_record_id",
      header: "Opportunity Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "name",
      header: "Account",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "account_source_record_id",
      header: "Account Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "stage_at_engagement",
      header: "Stage at Engagement",
    },
    {
      accessorKey: "current_stage",
      header: "Current Stage",
    },
    {
      accessorKey: "total_amount",
      header: "Opportunity Amount",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        return value !== null
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)
          : "—";
      },
    },
    {
      accessorKey: "created_date",
      meta: { dataType: "date" },
      header: "Created Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "close_date",
      meta: { dataType: "date" },
      header: "Close Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
    {
      accessorKey: "unique_people",
      header: "People",
    },
  ],
  opportunities_influenced_amount: [],
  opportunities_influenced_won: [
    {
      accessorKey: "name",
      header: "Opportunity",
      cell: ({ row }) => renderOpportunityLink(row.original),
    },
    {
      accessorKey: "opportunity_source_record_id",
      header: "Opportunity Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "name",
      header: "Account",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "account_source_record_id",
      header: "Account Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "stage_at_engagement",
      header: "Stage at Engagement",
    },
    {
      accessorKey: "current_stage",
      header: "Current Stage",
    },
    {
      accessorKey: "total_amount",
      header: "Opportunity Amount",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        return value !== null
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)
          : "—";
      },
    },
    {
      accessorKey: "created_date",
      meta: { dataType: "date" },
      header: "Created Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "close_date",
      meta: { dataType: "date" },
      header: "Close Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
    {
      accessorKey: "unique_people",
      header: "People",
    },
  ],
  opportunities_influenced_won_amount: [],
  opportunities_influenced_in_cycle: [
    {
      accessorKey: "name",
      header: "Opportunity",
      cell: ({ row }) => renderOpportunityLink(row.original),
    },
    {
      accessorKey: "opportunity_source_record_id",
      header: "Opportunity Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "name",
      header: "Account",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "account_source_record_id",
      header: "Account Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "stage_at_engagement",
      header: "Stage at Engagement",
    },
    {
      accessorKey: "current_stage",
      header: "Current Stage",
    },
    {
      accessorKey: "total_amount",
      header: "Opportunity Amount",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        return value !== null
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)
          : "—";
      },
    },
    {
      accessorKey: "created_date",
      meta: { dataType: "date" },
      header: "Created Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "close_date",
      meta: { dataType: "date" },
      header: "Close Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
    {
      accessorKey: "unique_people",
      header: "People",
    },
  ],
  opportunities_influenced_in_cycle_amount: [],
  opportunities_created_post_engagement: [
    {
      accessorKey: "name",
      header: "Opportunity",
      cell: ({ row }) => renderOpportunityLink(row.original),
    },
    {
      accessorKey: "opportunity_source_record_id",
      header: "Opportunity Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "name",
      header: "Account",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "account_source_record_id",
      header: "Account Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "current_stage",
      header: "Current Stage",
    },
    {
      accessorKey: "total_amount",
      header: "Opportunity Amount",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        return value !== null
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 0,
            }).format(value)
          : "—";
      },
    },
    {
      accessorKey: "days_till_creation",
      header: "Days from Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as number | null;
        return value !== null ? `${value} days` : "N/A";
      },
    },
    {
      accessorKey: "created_date",
      meta: { dataType: "date" },
      header: "Created Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "close_date",
      meta: { dataType: "date" },
      header: "Close Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "unique_people",
      header: "People",
    },
  ],
  opportunities_created_post_engagement_amount: [],
  deal_contacts_engaged: [
    {
      accessorKey: "contact",
      header: "Contact",
      cell: ({ row }) => {
        const firstName = row.original.first_name as string | null;
        const lastName = row.original.last_name as string | null;
        const email = row.original.email_address as string | null;
        return (
          <div className="flex flex-col">
            <span className="font-medium">
              {firstName && lastName ? `${firstName} ${lastName}` : "Unknown"}
            </span>
            <span className="text-sm text-muted-foreground">
              {email || "No email"}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "first_name",
      header: "First Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "last_name",
      header: "Last Name",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "source_record_id",
      header: "Person Source ID",
      meta: { exportOnly: true },
    },
    {
      accessorKey: "accounts",
      header: "Accounts",
      cell: ({ row }) => {
        const typedItem = row.original as {
          accounts?: Array<{
            id: string;
            name: string;
            source_record_id?: string;
          }>;
        };

        if (!typedItem.accounts || typedItem.accounts.length === 0) {
          return "-";
        }

        // Render all accounts as comma-separated links
        return (
          <span>
            {typedItem.accounts.map((account, idx) => (
              <React.Fragment key={account.id}>
                {idx > 0 && ", "}
                {renderAccountLink({
                  account_id: account.id,
                  name: account.name,
                })}
              </React.Fragment>
            ))}
          </span>
        );
      },
      meta: {
        exportValue: (item: DrilldownItem) => {
          const typedItem = item as {
            accounts?: AccountData[];
          };
          return formatAccountsForCSV(typedItem.accounts);
        },
      },
    },
    {
      accessorKey: "account_source_record_ids",
      header: "Account Source IDs",
      meta: {
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const typedItem = item as {
            accounts?: Array<{
              id: string;
              name: string;
              source_record_id?: string;
            }>;
          };
          return formatAccountSourceRecordIdsForCSV(typedItem.accounts);
        },
      },
    },
    {
      accessorKey: "job_title",
      header: "Job Title",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        if (!value)
          return <span className="italic text-muted-foreground">No data</span>;
        return <span>{value}</span>;
      },
    },
    {
      accessorKey: "relation_source",
      header: "Relation Source",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatRelationSource(value) : "";
      },
    },
    {
      accessorKey: "opportunities",
      header: "Opportunities",
      meta: {
        exportValue: (item: DrilldownItem) => {
          const typedItem = item as {
            opportunities_involved?: unknown;
            opportunities?: Array<{
              id: string;
              name: string;
              account_id: string;
            }>;
          };

          // Check for new JSON format first
          if (
            typedItem.opportunities &&
            Array.isArray(typedItem.opportunities)
          ) {
            return formatOpportunitiesForCSV(typedItem.opportunities);
          }

          // Fall back to legacy format
          return formatOpportunitiesForCSV(typedItem.opportunities_involved);
        },
      },
      cell: ({ row, getValue }) => {
        const value = getValue();
        const row_data = row.original as {
          opportunities_involved?: unknown;
          opportunity_ids?: unknown;
          opportunities?: Array<{
            id: string;
            name: string;
            account_id: string;
          }>;
          account_id?: string;
        };

        // Check for new JSON format first
        if (row_data.opportunities && Array.isArray(row_data.opportunities)) {
          if (row_data.opportunities.length === 0) return "-";

          return (
            <div className="flex flex-wrap gap-1">
              {row_data.opportunities.map((opportunity) => {
                const { id: oppId, name, account_id: accountId } = opportunity;
                return (
                  <Tooltip key={oppId}>
                    <TooltipTrigger asChild>
                      <a
                        href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-pill
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                          OPPORTUNITY_PILL_CLASSES,
                        )}
                      >
                        {name}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View this opportunity in Account Explorer</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          );
        }

        // Fall back to legacy format
        if (!value) return "-";

        const { names: opportunityNames, ids: opportunityIds } =
          getOpportunityData(row_data);
        const accountId = row_data.account_id;

        return (
          <div className="flex flex-wrap gap-1">
            {opportunityNames.map((name, index) => {
              const oppId = opportunityIds[index];

              if (oppId && accountId) {
                return (
                  <Tooltip key={oppId}>
                    <TooltipTrigger asChild>
                      <a
                        href={`/accounts/${accountId}/explorer?opportunityId=${oppId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-pill
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                          OPPORTUNITY_PILL_CLASSES,
                        )}
                      >
                        {name}
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View this opportunity in Account Explorer</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              // Fallback to non-clickable badge if no ID
              return (
                <span
                  key={index}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${CATEGORY_STYLES.Opportunity.color.replace(
                    " shadow-sm",
                    "",
                  )}`}
                >
                  {name}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
    },
    {
      accessorKey: "engagement_period",
      header: "Engagement Period",
      cell: ({ row }) => {
        const first = row.original.first_engagement_date as string | null;
        const last = row.original.last_engagement_date as string | null;
        return (
          <div className="flex flex-col">
            <span className="text-sm text-muted-foreground">
              First: {first ? formatDate(first) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">
              Last: {last ? formatDate(last) : "N/A"}
            </span>
          </div>
        );
      },
      meta: { exportable: false },
    },
    {
      accessorKey: "first_engagement_date",
      header: "First Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const first = item.first_engagement_date as string | null;
          return first ? formatDate(first) : "";
        },
      },
    },
    {
      accessorKey: "last_engagement_date",
      header: "Last Engagement",
      meta: {
        dataType: "date",
        exportOnly: true,
        exportValue: (item: DrilldownItem) => {
          const last = item.last_engagement_date as string | null;
          return last ? formatDate(last) : "";
        },
      },
    },
  ],
  accounts_activated: [
    {
      accessorKey: "name",
      header: "Account Name",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
      cell: ({ getValue }) => {
        const value = getValue() as number | undefined;
        return value !== undefined ? value : "-";
      },
    },
    {
      accessorKey: "unique_people",
      header: "People",
      cell: ({ getValue }) => {
        const value = getValue() as number | undefined;
        return value !== undefined ? value : "-";
      },
    },
    {
      accessorKey: "first_engagement_date",
      meta: { dataType: "date" },
      header: "First Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "last_engagement_date",
      meta: { dataType: "date" },
      header: "Last Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
  ],
  accounts_reactivated: [
    {
      accessorKey: "name",
      header: "Account Name",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "total_engagements",
      header: "Engagements",
      cell: ({ getValue }) => {
        const value = getValue() as number | undefined;
        return value !== undefined ? value : "-";
      },
    },
    {
      accessorKey: "unique_people",
      header: "People",
      cell: ({ getValue }) => {
        const value = getValue() as number | undefined;
        return value !== undefined ? value : "-";
      },
    },
    {
      accessorKey: "first_engagement_date",
      meta: { dataType: "date" },
      header: "First Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "last_engagement_date",
      meta: { dataType: "date" },
      header: "Last Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
  ],
  accounts_without_opps: [
    {
      accessorKey: "name",
      header: "Account Name",
      cell: ({ row }) => renderAccountLink(row.original),
    },
    {
      accessorKey: "total_engagements",
      header: "Total Engagements",
    },
    {
      accessorKey: "people_engaged",
      header: "People Engaged",
    },
    {
      accessorKey: "first_engagement_date",
      meta: { dataType: "date" },
      header: "First Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "last_engagement_date",
      meta: { dataType: "date" },
      header: "Last Engagement",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
  ],
  salesperson_active_days: [
    {
      accessorKey: "date",
      meta: { dataType: "date" },
      header: "Date",
      cell: ({ getValue }) => {
        const value = getValue() as string | null;
        return value ? formatDate(value) : "-";
      },
    },
    {
      accessorKey: "engagement_count",
      header: "Engagements",
    },
    {
      accessorKey: "unique_people",
      header: "Unique People",
    },
    {
      accessorKey: "cumulative_engagements",
      header: "Cumulative Engagements",
    },
    {
      accessorKey: "cumulative_people",
      header: "Cumulative People",
    },
  ],
  engagements_per_account: [],
  engagements_per_person: [],
  engagements_per_deal_contact: [],
};

// Default sort configurations based on campaign report card patterns
const METRIC_DEFAULT_SORTS: Record<
  SalespersonMetric,
  { key: string; direction: "asc" | "desc" }
> = {
  accounts_engaged: { key: "total_engagements", direction: "desc" },
  total_engagements: { key: "engagement_date", direction: "desc" },
  people_engaged: { key: "total_engagements", direction: "desc" },
  opportunities_influenced: { key: "created_date", direction: "desc" },
  opportunities_influenced_amount: { key: "total_amount", direction: "desc" },
  opportunities_influenced_won: { key: "created_date", direction: "desc" },
  opportunities_influenced_won_amount: {
    key: "total_amount",
    direction: "desc",
  },
  opportunities_influenced_in_cycle: { key: "created_date", direction: "desc" },
  opportunities_influenced_in_cycle_amount: {
    key: "total_amount",
    direction: "desc",
  },
  opportunities_created_post_engagement: {
    key: "total_amount",
    direction: "desc",
  },
  opportunities_created_post_engagement_amount: {
    key: "total_amount",
    direction: "desc",
  },
  deal_contacts_engaged: { key: "total_engagements", direction: "desc" },
  accounts_activated: { key: "last_engagement_date", direction: "desc" },
  accounts_reactivated: { key: "last_engagement_date", direction: "desc" },
  accounts_without_opps: { key: "total_engagements", direction: "desc" },
  salesperson_active_days: { key: "date", direction: "desc" }, // Campaign uses asc, but desc is better for recent activity
  engagements_per_account: { key: "total_engagements", direction: "desc" },
  engagements_per_person: { key: "total_engagements", direction: "desc" },
  engagements_per_deal_contact: { key: "total_engagements", direction: "desc" },
};

// Get default sort for a metric, with fallback
function getDefaultSort(
  metric: SalespersonMetric | null,
  columns: UnifiedTableColumn<DrilldownItem>[],
): { key: string; direction: "asc" | "desc" } {
  if (!metric || !METRIC_DEFAULT_SORTS[metric]) {
    // Fallback to first column descending
    return { key: String(columns[0]?.key || "name"), direction: "desc" };
  }

  const defaultSort = METRIC_DEFAULT_SORTS[metric];
  // Verify the column exists
  const columnExists = columns.some((col) => col.key === defaultSort.key);

  if (!columnExists) {
    // Fallback to first column if specified column doesn't exist
    return { key: String(columns[0]?.key || "name"), direction: "desc" };
  }

  return defaultSort;
}

export function MetricDrilldown({
  open,
  onOpenChange,
  personId,
  metric,
  touchpointTypes,
  filters,
  dateRange,
  includeBids = false,
  metrics,
  loadingStates,
  drillDownLimit,
}: MetricDrilldownProps) {
  const config = metric ? METRIC_CONFIG[metric] : null;
  const [resetTrigger, setResetTrigger] = useState<number | undefined>(
    undefined,
  );

  // Fetch salesperson details to get the name - must be called before any returns
  const { data: salesperson } = useSalesperson(personId || "");
  const metricValue = metric && metrics?.[metric];
  // Skip limit check for amount/currency metrics since they show dollar values, not row counts
  const isAmountMetric = metric?.includes("amount");
  const isDrillDownEnabled =
    isNumber(drillDownLimit) && isNumber(metricValue) && !isAmountMetric
      ? metricValue < drillDownLimit
      : true;

  const {
    data: drilldownData,
    isLoading,
    error,
  } = useSalespersonMetricDrilldown({
    personId: personId || "",
    metric: metric || "",
    touchpointTypes,
    filters,
    dateRange,
    limit: 1000,
    offset: 0,
    includeBids,
    isDrillDownEnabled,
  });

  // Get salesperson name if available
  const salespersonName = salesperson?.name;

  const columns = useMemo(() => {
    return metric ? METRIC_COLUMNS[metric] || [] : [];
  }, [metric]);

  // Convert ColumnDef to UnifiedTableColumn
  const unifiedColumns = useMemo(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    return columns
      .filter((col) => {
        // Filter out export-only columns for display
        const meta = (
          col as ColumnDef<DrilldownItem> & {
            meta?: { exportOnly?: boolean; dataType?: string };
          }
        ).meta;
        return !meta?.exportOnly;
      })
      .map((col) => {
        const colDef = col as ColumnDef<DrilldownItem> & {
          accessorKey?: string;
          meta?: { dataType?: string };
        };
        const columnKey = String(colDef.accessorKey || "");

        return {
          key: columnKey,
          title: String(colDef.header || ""),
          sortable: true,
          filterable: false,
          dataType: colDef.meta?.dataType,
          // Add sortKey for engagement_period columns to sort by last_engagement_date
          sortKey:
            columnKey === "engagement_period"
              ? "last_engagement_date"
              : undefined,
          render: colDef.cell
            ? (item: DrilldownItem) => {
                // Create a mock cell context for the cell function
                const mockCell = {
                  row: { original: item },
                  getValue: () => item[colDef.accessorKey as string],
                };
                // @ts-expect-error - Complex tanstack table types
                return colDef.cell(mockCell);
              }
            : undefined,
        } as UnifiedTableColumn<DrilldownItem>;
      });
  }, [columns]);

  // Get all columns including export-only for CSV export
  const allColumns = useMemo(() => {
    return columns.map((col) => {
      const colDef = col as ColumnDef<DrilldownItem> & { accessorKey?: string };
      const meta = (
        col as ColumnDef<DrilldownItem> & {
          meta?: {
            exportOnly?: boolean;
            exportable?: boolean;
            exportValue?: (item: DrilldownItem) => string;
            dataType?: string;
          };
        }
      ).meta;
      return {
        key: String(colDef.accessorKey || ""),
        title: String(colDef.header || ""),
        sortable: !meta?.exportOnly,
        exportOnly: meta?.exportOnly || false,
        filterable: false,
        exportable: meta?.exportable,
        dataType: meta?.dataType,
        exportValue: meta?.exportValue
          ? meta.exportValue
          : colDef.cell && !meta?.exportOnly
            ? (item: DrilldownItem) => {
                // For export, try to get raw value or use render function
                const value = item[colDef.accessorKey as string];
                if (value !== null && value !== undefined) {
                  return String(value);
                }
                // Create a mock cell context for the cell function
                const mockCell = {
                  row: { original: item },
                  getValue: () => value,
                };
                // @ts-expect-error - Complex tanstack table types
                const rendered = colDef.cell(mockCell);
                return String(rendered || "");
              }
            : (item: DrilldownItem) => {
                const value = item[colDef.accessorKey as string];
                return value !== null && value !== undefined
                  ? String(value)
                  : "";
              },
        render:
          colDef.cell && !meta?.exportOnly
            ? (item: DrilldownItem) => {
                // Create a mock cell context for the cell function
                const mockCell = {
                  row: { original: item },
                  getValue: () => item[colDef.accessorKey as string],
                };
                // @ts-expect-error - Complex tanstack table types
                return colDef.cell(mockCell);
              }
            : undefined,
      } as UnifiedTableColumn<DrilldownItem>;
    });
  }, [columns]);

  const {
    viewMode,
    setViewMode,
    cumulative,
    setCumulative,
    dateColumns,
    typedRows: typedDrilldownRows,
    hasDateColumns,
    showChartToggle,
    grouping,
    handleGroupingChange,
  } = useDrilldownChartState({
    metricKey: metric,
    drilldownColumns: allColumns,
    drilldownType: config?.drilldownType ?? null,
    rows: drilldownData?.results,
  });

  // Setup CSV export
  const { isExporting, exportData, defaultFilename } =
    useTableCSVExport<DrilldownItem>({
      data: drilldownData?.results
        ? (drilldownData.results as unknown as DrilldownItem[])
        : [],
      columns: allColumns || [],
      filename: `${salespersonName || personId}-${
        config?.title || metric
      }-${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Check if we have exportable data
  const hasExportableData = useMemo(() => {
    return (
      drilldownData?.results &&
      Array.isArray(drilldownData.results) &&
      drilldownData.results.length > 0
    );
  }, [drilldownData]);

  const tableData = (drilldownData?.results ??
    []) as unknown as DrilldownItem[];

  // Reset handler
  const handleResetTable = () => {
    setResetTrigger((prev) => (prev ?? -1) + 1);
  };

  // Early return after all hooks
  if (!metric || !personId) return null;

  // Error handling JSX
  if (error) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[90vw] max-w-full max-h-[90vh] flex flex-col overflow-hidden">
          <div className="p-4">
            <Alert variant="destructive">
              <AlertTitle>Error loading drilldown data</AlertTitle>
              <AlertDescription>
                {error.message ||
                  "Failed to load metric details. Please try again."}
              </AlertDescription>
            </Alert>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function renderContent() {
    // Check if drill down is disabled due to limit
    if (!isDrillDownEnabled && isNumber(drillDownLimit)) {
      return (
        <div className="flex justify-center items-center p-4 py-12">
          <DrilldownLimitAlert limit={drillDownLimit} />
        </div>
      );
    }
    if (config?.drilldownType === "chart") {
      return (
        <div className="p-4">
          <SalespersonEngagementChart
            data={
              (drilldownData?.results || []) as Array<{
                date: string;
                engagement_count: number;
                unique_people: number;
                cumulative_engagements?: number;
                cumulative_people?: number;
              }>
            }
            isLoading={isLoading}
          />
        </div>
      );
    }

    if (columns.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          Detailed view not available for this metric
        </div>
      );
    }

    // Chart mode for table-type metrics with date columns
    if (viewMode === "chart" && hasDateColumns && drilldownData) {
      return (
        <div className="h-full flex flex-col">
          <DrilldownChartErrorBoundary>
            <DrilldownTimeSeriesChart
              data={typedDrilldownRows}
              columns={dateColumns as unknown as UnifiedTableColumn[]}
              grouping={grouping}
              cumulative={cumulative}
            />
          </DrilldownChartErrorBoundary>
        </div>
      );
    }

    return (
      <div className="max-h-[calc(80vh-14rem)] overflow-y-auto">
        <SortableTable<DrilldownItem>
          tableName={`salespeople::drilldown::${metric}`}
          columns={unifiedColumns || []}
          data={tableData}
          keyExtractor={(item) => {
            const typedItem = item as Record<string, string | number>;
            // Use a combination of key fields for stability
            const keys = [
              typedItem.account_id,
              typedItem.opportunity_id,
              typedItem.person_id,
              typedItem.id,
              typedItem.touchpoint_id,
              typedItem.name,
            ].filter(Boolean);

            // If we have any keys, join them; otherwise use a stable fallback
            return keys.length > 0 ? keys.join("-") : "row";
          }}
          isLoading={isLoading}
          defaultSort={getDefaultSort(metric, unifiedColumns)}
          resetTrigger={resetTrigger}
          disableInternalScrolling={true}
          headerClassName="-top-px"
        />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[90vw] max-w-full max-h-[90vh] flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => {
          // Prevent the default behavior of focusing the first focusable element
          e.preventDefault();
          // Optionally focus the dialog title or content area instead
        }}
      >
        <DialogHeader className="flex flex-row items-end justify-between pb-2 space-y-0 pr-0">
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl font-semibold">
                {config?.title || "Metric Details"}
              </DialogTitle>
              {/* Compact Metric Card */}
              {metric && (
                <CompactMetricCard
                  metric={metric}
                  metrics={metrics}
                  loadingStates={loadingStates}
                />
              )}
            </div>

            {/* Tooltip Description */}
            {config?.tooltip && (
              <p className="text-sm text-muted-foreground">{config.tooltip}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <DrilldownChartControls
              grouping={grouping}
              onGroupingChange={handleGroupingChange}
              cumulative={cumulative}
              onCumulativeChange={setCumulative}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showChartControls={viewMode === "chart" && hasDateColumns}
              showChartToggle={showChartToggle}
            />

            {/* Reset Table Button - only show for table views */}
            {config?.drilldownType !== "chart" && viewMode === "table" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleResetTable}
                    className="h-8 w-8 bg-card"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset table to default state</p>
                </TooltipContent>
              </Tooltip>
            )}
            {hasExportableData && config?.drilldownType !== "chart" && (
              <ExportFilenameDialog
                open={isExportDialogOpen}
                onOpenChange={setIsExportDialogOpen}
                defaultFilename={defaultFilename}
                extension="csv"
                onExport={handleExport}
                disabled={isExporting}
              >
                <CSVExportButton
                  onClick={() => setIsExportDialogOpen(true)}
                  isExporting={isExporting}
                  tooltipText="Export as CSV"
                  disabled={isExporting}
                  className="bg-card"
                />
              </ExportFilenameDialog>
            )}
          </div>
        </DialogHeader>
        <div className="flex-grow rounded-md border bg-card">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
